import type { RenderModel } from "../renderModel";
import {
  VERSUS_PANEL_WIDTH,
  VERSUS_OPPONENT_X,
} from "../../versus/versusConstants";

// ── Types ────────────────────────────────────────────────────────────────

export interface Walker {
  dir: 1 | -1;
  progress: number;
  speed: number;
  delay: number;
  path: Array<{ x: number; y: number }>;
}

export interface CityBgCrop {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  dw: number;
  dh: number;
}

// ── Dependency interface ────────────────────────────────────────────────

export interface BackgroundDeps {
  ctx: CanvasRenderingContext2D;
  canvasWidth: number;
  canvasHeight: number;
  dpr: number;

  // Background images
  gameplayBgImage: HTMLImageElement | null;
  titleBgImage: HTMLImageElement | null;
  backstageBgImage: HTMLImageElement | null;
  dinerBgImage: HTMLImageElement | null;
  shopBgImage: HTMLImageElement | null;
  gemShopBgImage: HTMLImageElement | null;
  apartmentBgImage: HTMLImageElement | null;
  worldMapImage: HTMLImageElement | null;
  soloSelectBgImage: HTMLImageElement | null;
  soloModeBackgroundImages: Record<RenderModel["soloMode"], HTMLImageElement | null>;

  // Building overlays & walkers
  buildingImages: Record<string, HTMLImageElement | null>;
  buildingHover: Record<string, number>;
  walkingCharImages: Array<HTMLImageElement | null>;
  walkers: Walker[];

  // Mutable state (read + write)
  interiorEnterMs: number;
  prevWasInterior: boolean;
  cityBgCrop: CityBgCrop;

  // Delegate methods
  nowMs: () => number;
  drawBackgroundSection: (x: number, y: number, w: number, h: number) => void;
  getFrameLinearGradient: (
    key: string,
    createGradient: () => CanvasGradient,
  ) => CanvasGradient;
}

/** Return type so the caller can write mutable state back. */
export interface BackgroundMutableState {
  interiorEnterMs: number;
  prevWasInterior: boolean;
}

// ── Extracted screen function ───────────────────────────────────────────

export function drawBackgroundScreen(
  deps: BackgroundDeps,
  model: RenderModel,
): BackgroundMutableState {
  const {
    ctx,
    canvasWidth: cw,
    canvasHeight: ch,
  } = deps;
  const drawCoverFitImage = (
    image: HTMLImageElement,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
  ): void => {
    const imgAspect = image.naturalWidth / image.naturalHeight;
    const targetAspect = dw / dh;
    let sx = 0;
    let sy = 0;
    let sw = image.naturalWidth;
    let sh = image.naturalHeight;
    if (imgAspect < targetAspect) {
      const newH = sw / targetAspect;
      sy = (sh - newH) / 2;
      sh = newH;
    } else {
      const newW = sh * targetAspect;
      sx = (sw - newW) / 2;
      sw = newW;
    }
    ctx.drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh);
  };

  // Use city background for menu/lobby phases, circuit bg for gameplay
  const isMenuPhase =
    model.phase === "AUTH_GATE" ||
    model.phase === "TITLE" ||
    model.phase === "SETTINGS" ||
    model.phase === "LEADERBOARD" ||
    model.phase === "PROFILE" ||
    model.phase === "USERNAME_PICK" ||
    model.phase === "FRIENDS" ||
    model.phase === "ADD_FRIEND" ||
    model.phase === "SHOP" ||
    model.phase === "GEM_SHOP" ||
    model.phase === "TOKEN_SHOP" ||
    model.phase === "AD_GATE" ||
    model.phase === "ONBOARDING_CALL" ||
    model.phase === "PHONE";

  // Interior backgrounds for specific phases
  const isBattleSearch = (model.phase === "VERSUS_LOBBY" && model.versusQuickmatchActive) || model.phase === "VERSUS_COUNTDOWN";
  const isLobbyPhase = model.phase === "VERSUS_LOBBY" && !isBattleSearch;
  const isShopPhase = model.phase === "SHOP" || model.phase === "GEM_SHOP" || model.phase === "TOKEN_SHOP";
  const isProfilePhase = model.phase === "PROFILE";
  const isGameOver = model.phase === "RUN_GAMEOVER";
  const isSoloGameplayPhase =
    model.phase === "RUN_PLAYABLE"
    || model.phase === "RUN_RESOLVING"
    || model.phase === "RUN_LEVELUP";
  const isWorldMap = model.phase === "WORLD_MAP";
  const isSoloSelect = model.phase === "SOLO_SELECT";
  const isDailyRewards = model.phase === "DAILY_REWARDS" || model.phase === "DAILY_REWARDS_OPEN";
  const isInteriorPhase = isBattleSearch || isLobbyPhase || isShopPhase || isProfilePhase || isGameOver || isWorldMap || isSoloSelect || isDailyRewards;
  const selectedSoloModeBgImage = deps.soloModeBackgroundImages[model.selectedSoloMode] ?? deps.soloSelectBgImage;
  const soloRunBgImage = deps.soloModeBackgroundImages[model.soloMode] ?? deps.gameplayBgImage;
  const interiorImg = isBattleSearch
    ? deps.backstageBgImage
    : isLobbyPhase
      ? deps.dinerBgImage
      : (model.phase === "GEM_SHOP" || model.phase === "TOKEN_SHOP")
        ? (deps.gemShopBgImage ?? deps.shopBgImage)
        : isShopPhase
          ? deps.shopBgImage
          : (isProfilePhase || isDailyRewards)
            ? deps.apartmentBgImage
            : isGameOver
              ? soloRunBgImage
              : isWorldMap
                ? deps.worldMapImage
                : isSoloSelect
                  ? selectedSoloModeBgImage
                  : null;

  // Track interior entrance for zoom transition
  let interiorEnterMs = deps.interiorEnterMs;
  if (isInteriorPhase && !deps.prevWasInterior) {
    interiorEnterMs = performance.now();
  }
  const prevWasInterior = isInteriorPhase;

  if (isInteriorPhase && interiorImg != null) {
    // Zoom-in entrance transition (zoom from 1.3 to 1.0 over 600ms)
    const elapsed = performance.now() - interiorEnterMs;
    const zoomDur = 600;
    const zoomT = Math.min(1, elapsed / zoomDur);
    const eased = 1 - (1 - zoomT) * (1 - zoomT); // easeOutQuad
    const scale = 1.3 - 0.3 * eased;
    const fadeAlpha = eased;

    ctx.save();
    ctx.globalAlpha = fadeAlpha;
    const cx = cw / 2;
    const cy = ch / 2;
    ctx.translate(cx, cy);
    ctx.scale(scale, scale);
    ctx.translate(-cx, -cy);
    drawCoverFitImage(interiorImg, 0, 0, cw, ch);
    ctx.restore();

    // Darken slightly for UI legibility (skip for world map)
    if (!isWorldMap) {
      ctx.fillStyle = "rgba(0,0,0,0.25)";
      ctx.fillRect(0, 0, cw, ch);
    }
  } else if (isSoloGameplayPhase && soloRunBgImage != null) {
    drawCoverFitImage(soloRunBgImage, 0, 0, cw, ch);
  } else if (isMenuPhase && !isShopPhase && !isProfilePhase && deps.titleBgImage != null) {
    // Cover-fit the city bg: fill canvas width, crop vertically from top
    const imgAspect = deps.titleBgImage.naturalWidth / deps.titleBgImage.naturalHeight;
    const canvasAspect = cw / ch;
    let sw = deps.titleBgImage.naturalWidth;
    let sh = deps.titleBgImage.naturalHeight;
    let sx = 0;
    let sy = 0;
    if (imgAspect < canvasAspect) {
      // Image is taller — crop top/bottom
      const newH = sw / canvasAspect;
      sy = (sh - newH) / 2;
      sh = newH;
    } else {
      // Image is wider — crop sides
      const newW = sh * canvasAspect;
      sx = (sw - newW) / 2;
      sw = newW;
    }
    const onboardingFocus = model.phase === "ONBOARDING_CALL" && model.onboardingStep?.highlightBuilding != null;
    if (onboardingFocus) {
      ctx.save();
      ctx.filter = "grayscale(0.85) brightness(0.5)";
      ctx.globalAlpha = 0.7;
    }
    ctx.drawImage(deps.titleBgImage, sx, sy, sw, sh, 0, 0, cw, ch);
    if (onboardingFocus) {
      ctx.restore();
    }
    // Darken slightly so UI text remains legible
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.fillRect(0, 0, cw, ch);
    // Draw building overlays + walking characters on TITLE with layering:
    if (model.phase === "TITLE" || model.phase === "ONBOARDING_CALL") {
      const timeSec = performance.now() / 1000;
      const bDefs: Array<{ key: string; glowColor: string }> = [
        { key: "battle", glowColor: "#ff6b6b" },
        { key: "shop",   glowColor: "#d4a8ff" },
        { key: "solo",   glowColor: "#64d8ff" },
        { key: "lobby",  glowColor: "#5c8cd4" },
        { key: "apartment", glowColor: "#c79dff" },
      ];

      const drawBuilding = (bdef: { key: string; glowColor: string }, i: number) => {
        const img = deps.buildingImages[bdef.key];
        if (!img) return;
        const outOfRamen = bdef.key === "battle" && model.staminaCurrent <= 0 && !model.staminaPremiumActive;
        const onboardingDim = model.onboardingStep?.highlightBuilding != null && bdef.key !== model.onboardingStep.highlightBuilding;
        const locked =
          (bdef.key === "shop" && !model.featureLocks.shop.unlocked) ||
          (bdef.key === "lobby" && !model.featureLocks.lobby.unlocked) ||
          outOfRamen ||
          onboardingDim;
        const hover = deps.buildingHover[bdef.key] ?? 0;

        if (locked) {
          ctx.save();
          ctx.filter = "grayscale(0.85) brightness(0.5)";
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
          ctx.restore();
        } else {
          const baseGlow = 0.15 + 0.1 * Math.sin(timeSec * 2.5 + i * 1.2);
          const glowAlpha = baseGlow + hover * 0.6;
          const baseBlur = 8 + 4 * Math.sin(timeSec * 3 + i);
          const glowBlur = baseBlur + hover * 20;
          ctx.save();
          ctx.shadowColor = bdef.glowColor;
          ctx.shadowBlur = glowBlur;
          ctx.globalAlpha = glowAlpha;
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
          ctx.restore();
          ctx.save();
          ctx.globalAlpha = 1;
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
          if (hover > 0) {
            ctx.globalAlpha = hover * 0.2;
            ctx.globalCompositeOperation = "lighter";
            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
          }
          ctx.restore();
        }
      };

      const bgBuildings = new Set(["battle", "apartment"]);

      // Layer 1: background buildings (behind characters)
      for (let i = 0; i < bDefs.length; i++) {
        if (bgBuildings.has(bDefs[i]!.key)) drawBuilding(bDefs[i]!, i);
      }

      // Layer 2: walking characters (enter from one side, exit the other)
      const dt = 1 / 60;
      for (let wi = 0; wi < deps.walkers.length; wi++) {
        const charImg = deps.walkingCharImages[wi];
        if (!charImg) continue;
        const wk = deps.walkers[wi]!;
        // Delay before spawning
        if (wk.delay > 0) { wk.delay -= dt; continue; }
        wk.progress += wk.speed * dt;
        if (wk.progress >= 1) {
          // Off-screen — reset with random delay before next pass
          wk.progress = 0;
          wk.delay = 4 + Math.random() * 8;
          continue;
        }
        const totalSegs = wk.path.length - 1;
        const segFloat = wk.progress * totalSegs;
        const segIdx = Math.min(Math.floor(segFloat), totalSegs - 1);
        const segT = segFloat - segIdx;
        const p0 = wk.path[segIdx]!;
        const p1 = wk.path[segIdx + 1]!;
        const px = p0.x + (p1.x - p0.x) * segT;
        const py = p0.y + (p1.y - p0.y) * segT;
        const charH = ch * 0.04;
        const charW = charH * (charImg.naturalWidth / charImg.naturalHeight);
        const charX = px * cw - charW / 2;
        const charY = py * ch - charH;
        ctx.save();
        if (wk.dir === -1) {
          ctx.translate(charX + charW, charY);
          ctx.scale(-1, 1);
          ctx.drawImage(charImg, 0, 0, charW, charH);
        } else {
          ctx.drawImage(charImg, charX, charY, charW, charH);
        }
        ctx.restore();
      }

      // Layer 3: foreground buildings (in front of characters)
      for (let i = 0; i < bDefs.length; i++) {
        if (!bgBuildings.has(bDefs[i]!.key)) drawBuilding(bDefs[i]!, i);
      }

    }

    deps.cityBgCrop.sx = sx;
    deps.cityBgCrop.sy = sy;
    deps.cityBgCrop.sw = sw;
    deps.cityBgCrop.sh = sh;
    deps.cityBgCrop.dw = cw;
    deps.cityBgCrop.dh = ch;
  } else if (model.phase === "VERSUS_MATCH") {
    // Render each player panel with the same wallpaper framing.
    deps.drawBackgroundSection(0, 0, VERSUS_PANEL_WIDTH, ch);
    deps.drawBackgroundSection(VERSUS_OPPONENT_X, 0, VERSUS_PANEL_WIDTH, ch);
    const dividerW = Math.max(0, VERSUS_OPPONENT_X - VERSUS_PANEL_WIDTH);
    if (dividerW > 0) {
      deps.drawBackgroundSection(VERSUS_PANEL_WIDTH, 0, dividerW, ch);
    }
  } else {
    deps.drawBackgroundSection(0, 0, cw, ch);
  }

  if (model.run?.groove.active) {
    const t = deps.nowMs() / 140;
    const hueA = (t * 35) % 360;
    const hueB = (hueA + 90) % 360;
    const pulse = 0.35 + 0.25 * Math.sin(t);
    const grooveGrad = deps.getFrameLinearGradient(
      `bg-groove|${cw}|${ch}|${hueA.toFixed(1)}|${hueB.toFixed(1)}|${pulse.toFixed(3)}`,
      () => {
        const gradient = ctx.createLinearGradient(0, 0, cw, ch);
        gradient.addColorStop(0, `hsla(${hueA.toFixed(1)},95%,52%,${pulse.toFixed(3)})`);
        gradient.addColorStop(1, `hsla(${hueB.toFixed(1)},95%,52%,${(pulse * 0.85).toFixed(3)})`);
        return gradient;
      }
    );
    ctx.fillStyle = grooveGrad;
    ctx.fillRect(0, 0, cw, ch);
  }

  return { interiorEnterMs, prevWasInterior };
}
